import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Grid,
} from "@mui/material";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";

import * as actions from "../../redux/actions/index";
import { onFilterParams } from "../../utils/filterParams";
import { formatDate } from "../../utils/formatDate";
import { CustomSelect } from "../CustomSelect/CustomSelect.component";
import { customerOption } from "../../utils/optionsUtils";
import { selectStyles } from "../CustomSelect/selectStyles";

const Filter = ({ page }) => {
  const dispatch = useDispatch();
  const cmsAuth = useSelector((state) => state.cmsAuthenticator.cmsAuth || "");
  const { control, handleSubmit, setValue } = useForm();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [customerFilter, setCustomerFilter] = useState("");
  const [customerMultiFilter, setCustomerMultiFilter] = useState(null);
  const [hasTransactions, setHasTransactions] = useState(false);
  const [hasRecurringToken, setHasRecurringToken] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [adsFilter, setAdsFilter] = useState("");
  const role = cmsAuth.role.toLowerCase().replace("_", "");

  useEffect(() => {
    if (
      hasTransactions === true ||
      hasRecurringToken === true ||
      hasRecurringToken === true
    ) {
      setValue("search", true);
    } else {
      setValue("search", "");
    }
  }, [hasTransactions]);

  useEffect(() => {
    const storedParams = sessionStorage.getItem(`${page}_filterParams`);

    if (storedParams) {
      const parsedParams = JSON.parse(storedParams);
      const params = new URLSearchParams(parsedParams);
      console.log("apara", params);

      const handleDateParams = () => {
        const startDate = params.get("startDate");
        const endDate = params.get("endDate");

        if (startDate) setStartDate(startDate);
        if (endDate) setEndDate(endDate);
      };

      const handleMultiParams = (setFilter, setSearch, setMultiFilter) => {
        const filterParamName = params.keys().next().value; // Gets the first key
        const filter = params.get(filterParamName); // Get the value based on the parameter name

        // Retrieve individual multiFilter values
        const hasTransactions = params.get("has_transactions") === "true";
        const hasRecurringToken = params.get("has_recurring_token") === "true";
        const isRegistered = params.get("is_registered") === "true";

        const search = filter;

        if (search) setSearch(search);
        if (filter) setFilter(filterParamName);

        // Collect non-null multiFilter values into an array
        const multiFilter = [];
        if (hasTransactions)
          multiFilter.push({
            value: "has_transactions",
            label: "Has Transactions",
            checked: hasTransactions,
          });
        if (hasRecurringToken)
          multiFilter.push({
            value: "has_recurring_token",
            label: "Has Recurring Token",
            checked: hasRecurringToken,
          });
        if (isRegistered)
          multiFilter.push({
            value: "is_registered",
            label: "Is Registered",
            checked: isRegistered,
          });

        setMultiFilter(multiFilter);
      };

      const handleSearchParams = (setSearch) => {
        const search = params.get("text");
        if (search) setSearch(search);
      };

      const handleStatusParam = () => {
        const status = params.get("status");
        if (status) setValue("status", status);
      };

      const handleAdsParam = () => {
        const filterParamName = params.keys().next().value; // Gets the first key
        const filter = params.get(filterParamName);

        setValue("search", filter);
        setAdsFilter(filterParamName);
      };

      switch (page) {
        case "dashboard":
          handleDateParams();
          break;
        case "transactions":
          handleDateParams();
          handleSearchParams((search) => setValue("search", search));
          handleStatusParam();
          break;
        case "customers":
          handleMultiParams(
            setCustomerFilter,
            (search) => setValue("search", search),
            setCustomerMultiFilter
          );
          break;
        case "ads":
          handleDateParams();
          handleAdsParam();
          break;
        default:
          break;
      }
    }
  }, [page, setValue]);

  const formatDateChange = (dateArg, which) => {
    formatDate(dateArg, which, setStartDate, setEndDate);
  };

  const handleChangeCustomer = (event) => {
    setCustomerFilter(event.target.value);
  };

  const handleChangeMultiFilter = (selectedOptions) => {
    setCustomerMultiFilter(selectedOptions);

    let hasTransactionsSelected = false;
    let hasRecurringTokenSelected = false;
    let isRegisteredSelected = false;

    selectedOptions.forEach((option) => {
      if (option.value === "has_transactions") {
        hasTransactionsSelected = true;
      } else if (option.value === "has_recurring_token") {
        hasRecurringTokenSelected = true;
      } else if (option.value === "is_registered") {
        isRegisteredSelected = true;
      }
    });
    setHasTransactions(hasTransactionsSelected ? true : null);
    setHasRecurringToken(hasRecurringTokenSelected ? true : null);
    setIsRegistered(isRegisteredSelected ? true : null);
  };

  const handleChangeAds = (event) => {
    setAdsFilter(event.target.value);
  };

  const onFilterSubmit = (data) => {
    let params = onFilterParams(
      page,
      data.search,
      startDate,
      endDate,
      data.status,
      customerFilter,
      hasTransactions,
      hasRecurringToken,
      isRegistered
    );

    sessionStorage.setItem(`${page}_filterParams`, JSON.stringify(params)); // Store the filter parameters in session

    if (page === "dashboard") {
      dispatch(actions.onDashboardDatas(params));
      dispatch(actions.onFilterDashboard());
    } else if (page === "transactions") {
      dispatch(actions.onFetchTransactions(0, 20, role, params));
      dispatch(actions.onFilterTransactions());
    } else if (page === "customers") {
      dispatch(actions.onFetchCustomers(0, 20, params));
      dispatch(actions.onFilterCustomers());
    } else if (page === "ads") {
      dispatch(actions.onFetchAds(0, 20, "-created_at", params));
      dispatch(actions.onFilterAds());
    } else {
      return false;
    }
  };

  // EXPORT
  const dashboardExport = () => {
    return dispatch(actions.onDashboardExport(startDate, endDate, cmsAuth.id));
  };

  // RESET
  const resetFilters = (key, additionalReset = () => { }) => {
    sessionStorage.removeItem(key);
    setStartDate(null);
    setEndDate(null);
    setValue("search", "");
    setValue("status", "any");
    additionalReset();
  };

  const onDashboardReset = () => {
    resetFilters(`${page}_filterParams`);
  };

  const onTransactionsReset = () => {
    resetFilters(`${page}_filterParams`);
  };

  const onCustomersReset = () => {
    resetFilters(`${page}_filterParams`, () => {
      setCustomerFilter("");
      setCustomerMultiFilter(null);
      setHasTransactions(false);
      setHasRecurringToken(false);
      setIsRegistered(false);
    });
  };
  const onAdsReset = () => {
    resetFilters(`${page}_filterParams`, () => {
      setAdsFilter("");
    });
  };

  // FILE EXPORT
  const adsExport = (e) => {
    return dispatch(actions.onAdsExport(startDate, endDate));
  };

  const filterContent = (pageLocation) => {
    let filter = null;
    if (pageLocation === "dashboard") {
      return (filter = (
        <form className="Filter__form">
          <Box sx={{ flexGrow: 1 }}>
            <Grid
              container
              spacing={2}
              style={{ justifyContent: "center", alignItems: "center" }}
            >
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <FormControl fullWidth>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      value={startDate ? dayjs(startDate) : null}
                      label="Start Date"
                      onChange={(d) => formatDateChange(d, "start")}
                    />
                  </LocalizationProvider>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <FormControl fullWidth>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      value={endDate ? dayjs(endDate) : null}
                      label="End Date"
                      onChange={(d) => formatDateChange(d, "end")}
                    />
                  </LocalizationProvider>
                </FormControl>
              </Grid>
              <Grid item xs={4} sm={4} md={1} lg={1}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  type="submit"
                  onClick={handleSubmit(onFilterSubmit)}
                >
                  FILTER
                </Button>
              </Grid>
              <Grid item xs={4} sm={4} md={1} lg={1}>
                <Button
                  fullWidth
                  variant="contained"
                  color="secondary"
                  onClick={onDashboardReset}
                >
                  RESET
                </Button>
              </Grid>
              <Grid item xs={4} sm={4} md={1} lg={1}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={dashboardExport}
                >
                  EXPORT
                </Button>
              </Grid>
            </Grid>
          </Box>
        </form>
      ));
    } else if (pageLocation === "transactions") {
      return (filter = (
        <form className="Filter__form">
          <Box sx={{ flexGrow: 1 }}>
            <Grid
              container
              spacing={2}
              style={{ alignItems: "center", justifyContent: "center" }}
            >
              <Grid item xs={12} sm={6} md={2} lg={2.5}>
                <FormControl fullWidth>
                  <Controller
                    name="search"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="text"
                        placeholder="Search"
                        label="Search"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={2.5}>
                <FormControl fullWidth>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      value={startDate ? dayjs(startDate) : null}
                      label="Start Date"
                      onChange={(d) => formatDateChange(d, "start")}
                    />
                  </LocalizationProvider>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={2.5}>
                <FormControl fullWidth>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      value={endDate ? dayjs(endDate) : null}
                      label="End Date"
                      onChange={(d) => formatDateChange(d, "end")}
                    />
                  </LocalizationProvider>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2} lg={1.5}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Controller
                    name="status"
                    control={control}
                    defaultValue={"any"}
                    render={({ field }) => (
                      <Select {...field} fullWidth>
                        <MenuItem value="any">ANY</MenuItem>
                        <MenuItem value="valid">VALID</MenuItem>
                        <MenuItem value="invalid">INVALID</MenuItem>
                        <MenuItem value="pending">PENDING</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={6} md={1}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  type="submit"
                  onClick={handleSubmit(onFilterSubmit)}
                >
                  Filter
                </Button>
              </Grid>
              <Grid item xs={6} sm={6} md={1}>
                <Button
                  fullWidth
                  variant="contained"
                  color="secondary"
                  onClick={onTransactionsReset}
                >
                  RESET
                </Button>
              </Grid>
            </Grid>
          </Box>
        </form>
      ));
    } else if (pageLocation === "customers") {
      return (filter = (
        <form className="Filter__form">
          <Box sx={{ flexGrow: 1 }}>
            <Grid
              container
              spacing={2}
              style={{ alignItems: "center", justifyContent: "center" }}
            >
              <Grid item xs={12} sm={4} md={3} lg={2}>
                <FormControl fullWidth>
                  <Controller
                    name="search"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="text"
                        placeholder="Search"
                        label="Search"
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4} md={3} lg={2}>
                <FormControl fullWidth>
                  <InputLabel>Select a Filter</InputLabel>
                  <Select
                    value={customerFilter}
                    label="Filter"
                    onChange={handleChangeCustomer}
                    fullWidth
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value="name">Name</MenuItem>
                    <MenuItem value="recurring_token">Recurring Token</MenuItem>
                    <MenuItem value="last_action">Last Action</MenuItem>
                    <MenuItem value="recurring_expiry">
                      Recurring Expiry
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} md={3} lg={3}>
                <FormControl fullWidth>
                  <CustomSelect
                    options={customerOption}
                    isMulti={true}
                    onChange={handleChangeMultiFilter}
                    value={customerMultiFilter}
                    customStyles={selectStyles}
                    placeholder="Select Multiple Filters"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={6} md={1.5}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  type="submit"
                  onClick={handleSubmit(onFilterSubmit)}
                >
                  Filter
                </Button>
              </Grid>
              <Grid item xs={6} sm={6} md={1.5}>
                <Button
                  fullWidth
                  variant="contained"
                  color="secondary"
                  onClick={onCustomersReset}
                >
                  RESET
                </Button>
              </Grid>
            </Grid>
          </Box>
        </form>
      ));
    } else if (pageLocation === "ads") {
      return (filter = (
        <form className="Filter__form">
          <Box sx={{ flexGrow: 1 }} className="wrapper">
            <Grid container spacing={2} style={{ alignItems: "center" }}>
              <Grid item xs={6} lg={4}>
                <FormControl fullWidth>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      value={startDate ? dayjs(startDate) : null}
                      label="Start Date"
                      onChange={(d) => formatDateChange(d, "start")}
                      style={{ width: "100%" }}
                    />
                  </LocalizationProvider>
                </FormControl>
              </Grid>
              <Grid item xs={6} lg={4}>
                <FormControl fullWidth>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      value={endDate ? dayjs(endDate) : null}
                      label="End Date"
                      onChange={(d) => formatDateChange(d, "end")}
                      style={{ width: "100%" }}
                    />
                  </LocalizationProvider>
                </FormControl>
              </Grid>
            </Grid>
            <Grid
              container
              spacing={2}
              style={{
                justifyContent: "center",
                alignItems: "center",
                marginTop: "-9px",
              }}
            >
              <Grid item xs={6} lg={4}>
                <Controller
                  name="search"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="text"
                      placeholder="Search"
                      label="Search"
                      style={{ width: "100%" }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6} lg={4}>
                <FormControl fullWidth>
                  <InputLabel>Filter by:</InputLabel>
                  <Select
                    value={adsFilter}
                    label="Filter"
                    onChange={handleChangeAds}
                  >
                    <MenuItem value="ad_id">Ad Id</MenuItem>
                    <MenuItem value="ad_title">Ad Name</MenuItem>
                    <MenuItem value="ref">Registration Source ID</MenuItem>
                    <MenuItem value="source">Source</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} lg={4}>
                <Grid container spacing={2} style={{ alignItems: "center" }}>
                  <Grid item xs={4}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      type="submit"
                      onClick={handleSubmit(onFilterSubmit)}
                    >
                      Filter
                    </Button>
                  </Grid>
                  <Grid item xs={4}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="secondary"
                      onClick={onAdsReset}
                    >
                      RESET
                    </Button>
                  </Grid>
                  <Grid item xs={4}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={adsExport}
                    >
                      EXPORT
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </form>
      ));
    }
    return filter;
  };

  const filter = filterContent(page);
  return <div className="Filter">{filter}</div>;
};

export default Filter;
